import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Target, Zap, ShieldCheck, Search, ClipboardCheck } from 'lucide-react';
import RfPageFrame from '../layout/RfPageFrame';

export default function ServicesAiSecurity() {
  return (
    <>
      <div className="rf-hero-band">
        <RfPageFrame className="!py-10 md:!py-14">
          <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5c6d7e] mb-2">Services · AI Security</p>
          <h1 className="text-2xl md:text-[2.25rem] font-semibold text-[#2c2c2c] tracking-tight max-w-3xl leading-tight">
            Comprehensive security assessments and risk insight for AI systems
          </h1>
          <p className="mt-6 text-[1.05rem] text-[#5c5c5c] max-w-2xl leading-relaxed">
            We provide structured testing, repeatable runs, and risk registers your stakeholders can use—centred on our 
            ScanFort and RiskFort service lines. Protect your generative AI and machine learning models from emerging threats.
          </p>
        </RfPageFrame>
      </div>

      <section className="bg-white border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          {/* Overview Section */}
          <div className="space-y-6">
            <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c]">Service Overview</h2>
            <p className="text-[#5c5c5c] leading-relaxed max-w-4xl">
              RakFort's AI Security services are designed to address the unique vulnerabilities of artificial intelligence. 
              From prompt injection and jailbreaking to data poisoning and model inversion, we help you identify, 
              measure, and mitigate risks across the entire AI lifecycle.
            </p>
          </div>
        </RfPageFrame>
      </section>

      <section className="bg-[#f0f7ff] border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          {/* What we offer - Grid style */}
          <div className="space-y-8">
            <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c]">Our Specialized AI Security Lines</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="rf-card p-8 space-y-4 bg-white">
                <div className="w-12 h-12 bg-[#005a9c]/10 rounded-lg flex items-center justify-center">
                  <Search className="w-6 h-6 text-[#005a9c]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2c2c2c]">ScanFort — Red Teaming & Assessment</h3>
                <p className="text-[#5c5c5c] text-[0.95rem] leading-relaxed">
                  Broaden coverage beyond manual exercises with libraries of attacks, repeatable runs, and traceable results. 
                  We simulate sophisticated adversarial attacks to stress-test your AI defenses.
                </p>
                <Link to="/scanfort" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c] hover:underline">
                  Explore ScanFort <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="rf-card p-8 space-y-4 bg-white">
                <div className="w-12 h-12 bg-[#005a9c]/10 rounded-lg flex items-center justify-center">
                  <ClipboardCheck className="w-6 h-6 text-[#005a9c]" />
                </div>
                <h3 className="text-lg font-semibold text-[#2c2c2c]">RiskFort — AI Risk Management</h3>
                <p className="text-[#5c5c5c] text-[0.95rem] leading-relaxed">
                  Centralise risk across models and use cases. Track treatment, owners, and evidence for governance 
                  forums with our structured risk registers and scoring systems.
                </p>
                <Link to="/riskfort" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c] hover:underline">
                  Explore RiskFort <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </RfPageFrame>
      </section>

      <section className="bg-white border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          {/* Why Choose Us - Pillars */}
          <div className="space-y-8">
            <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c]">Why Choose RakFort for AI Security?</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-3">
                <ShieldCheck className="w-8 h-8 text-[#005a9c] opacity-80" />
                <h4 className="font-semibold text-[#2c2c2c]">Technical Rigor</h4>
                <p className="text-sm text-[#5c5c5c] leading-relaxed">
                  Our methodologies are based on the latest research and aligned with standards like OWASP Top 10 for LLMs 
                  and MITRE ATLAS.
                </p>
              </div>
              <div className="space-y-3">
                <Zap className="w-8 h-8 text-[#005a9c] opacity-80" />
                <h4 className="font-semibold text-[#2c2c2c]">Operational Speed</h4>
                <p className="text-sm text-[#5c5c5c] leading-relaxed">
                  Automated scanning and structured reporting mean you can move from assessment to remediation in 
                  days, not weeks.
                </p>
              </div>
              <div className="space-y-3">
                <Target className="w-8 h-8 text-[#005a9c] opacity-80" />
                <h4 className="font-semibold text-[#2c2c2c]">Business Context</h4>
                <p className="text-sm text-[#5c5c5c] leading-relaxed">
                  We don't just find bugs; we translate technical vulnerabilities into business impact and actionable 
                  risk registers.
                </p>
              </div>
            </div>
          </div>
        </RfPageFrame>
      </section>

      <section className="bg-[#f0f7ff] border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          {/* Our Approach */}
          <div className="rf-card p-8 md:p-10 bg-white border-dashed border-2">
            <h2 className="text-xl font-semibold text-[#2c2c2c] mb-6">Our Engagement Approach</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="font-bold text-[#005a9c] text-lg opacity-40 shrink-0">01</div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c]">Scoping & Threat Modeling</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1">We define the boundaries and identify the most likely attack vectors for your specific AI implementation.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="font-bold text-[#005a9c] text-lg opacity-40 shrink-0">02</div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c]">Automated & Manual Testing</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1">Using ScanFort alongside expert manual red teaming to uncover deep-seated vulnerabilities.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="font-bold text-[#005a9c] text-lg opacity-40 shrink-0">03</div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c]">Risk Quantification</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1">Findings are fed into RiskFort to provide a clear, prioritized view of your exposure.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="font-bold text-[#005a9c] text-lg opacity-40 shrink-0">04</div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c]">Remediation Guidance</h4>
                  <p className="text-sm text-[#5c5c5c] mt-1">Practical, engineering-focused advice on how to fix vulnerabilities and harden your AI systems.</p>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-12 text-[0.9375rem] text-[#5c5c5c]">
            Need a tailored statement of work?{' '}
            <Link to="/contactus" className="text-[#005a9c] font-medium hover:text-[#004a82]">
              Contact us for a consultation
            </Link>
            .
          </p>
        </RfPageFrame>
      </section>
    </>
  );
}
